// ── Root App shell — routes, top bar, sidebar, command palette, AI launcher ──
// Pulled out of index.html so Vite can bundle it cleanly. Components it uses
// (VaultPage, TearsheetPage, …) are still attached to window.* by their
// respective .jsx files (which main.jsx side-effect-imports before mount).

function FeedbackForm({ onClose, onToast }) {
  const [type, setType]       = React.useState("Bug");
  const [body, setBody]       = React.useState("");
  const [submitting, setSub]  = React.useState(false);

  const TYPES = ["Bug", "Feature request", "Performance", "UX", "Other"];

  const submit = async () => {
    if (!body.trim()) return;
    setSub(true);
    try {
      const session = await window.gqAuth.getSession();
      const user_id = session?.user?.id || null;
      const page    = window.location.hash.slice(1) || "vault";
      const { error } = await window.sbClient.from("feedback").insert({ user_id, type, body: body.trim(), page });
      if (error) throw error;
      onToast("Feedback sent — thank you!");
      onClose();
    } catch (e) {
      console.error("[feedback]", e);
      onToast("Failed to send feedback. Please try again.");
    } finally {
      setSub(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--muted)", display: "block", marginBottom: 5 }}>Type</label>
        <select value={type} onChange={e => setType(e.target.value)} style={{
          width: "100%", padding: "8px 10px", background: "var(--bg-deep)",
          border: "1px solid var(--border-strong)", borderRadius: "var(--r-md)",
          color: "var(--ink)", fontFamily: "var(--font-ui)", fontSize: 13, outline: "none",
        }}>
          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--muted)", display: "block", marginBottom: 5 }}>Details</label>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={4} placeholder="Describe the issue or idea…" style={{
          width: "100%", padding: "8px 10px", background: "var(--bg-deep)",
          border: "1px solid var(--border-strong)", borderRadius: "var(--r-md)",
          color: "var(--ink)", fontFamily: "var(--font-ui)", fontSize: 13,
          resize: "vertical", outline: "none", boxSizing: "border-box",
        }}/>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={{
          background: "none", border: "1px solid var(--border-strong)", borderRadius: "var(--r-md)",
          padding: "7px 14px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 13, color: "var(--muted)",
        }}>Cancel</button>
        <button onClick={submit} disabled={submitting || !body.trim()} style={{
          background: "var(--primary)", border: "none", borderRadius: "var(--r-md)",
          padding: "7px 16px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 13,
          color: "#fff", fontWeight: 600, opacity: (submitting || !body.trim()) ? 0.5 : 1,
        }}>{submitting ? "Sending…" : "Send feedback"}</button>
      </div>
    </div>
  );
}

function App() {
  const [route, setRoute]     = React.useState("vault");
  const [drawer, setDrawer]   = React.useState(false);
  const [role, setRole]       = React.useState(null);
  const [authChecked, setAC]  = React.useState(false);
  const [chatOpen, setChatOpen] = React.useState(false);
  const [tourOpen, setTourOpen] = React.useState(false);
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [ToastEl, fireToast]  = useToast();

  // Auth guard — real Supabase session check
  React.useEffect(() => {
    (async () => {
      try {
        const guard = await window.gqAuth.guard("auth.html", "pending.html");
        if (!guard) return; // guard() already redirected
        setRole(guard.role);
        setAC(true);
        // Identify the user to Sentry + PostHog
        window.GQ?.observability?.identify(guard.user.id, { email: guard.user.email, role: guard.role });
        if (localStorage.getItem("gq_tour_completed") !== "1") setTourOpen(true);
      } catch (e) {
        console.error("[auth] guard failed", e);
        window.location.href = "auth.html";
      }
    })();
  }, []);

  // PostHog pageview on route change
  React.useEffect(() => {
    if (authChecked) window.GQ?.observability?.pageview?.(route);
  }, [route, authChecked]);

  // Global keyboard shortcuts
  React.useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setPaletteOpen(o => !o); return; }
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "?") { e.preventDefault(); setShortcutsOpen(true); }
      else if (e.key === "a") { e.preventDefault(); setChatOpen(true); }
      else if (e.key === "Escape") { setPaletteOpen(false); setShortcutsOpen(false); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Hash routing — supports #page?run=<uuid> via GQ.state.parseHash
  React.useEffect(() => {
    const onHash = () => {
      const parsed = window.GQ?.state?.parseHash() || { page: window.location.hash.slice(1), params: {} };
      if (parsed.page) setRoute(parsed.page);
    };
    window.addEventListener("hashchange", onHash);
    onHash();
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Hydrate pipeline state from Supabase on mount
  React.useEffect(() => {
    if (window.GQ?.state?.hydrateFromSupabase) {
      window.GQ.state.hydrateFromSupabase().catch(e => {
        console.warn('[app] pipeline state hydration failed', e);
      });
    }
  }, []);

  // Listen for AI tool executions and surface a toast even if the chat is closed
  React.useEffect(() => {
    const handler = (e) => {
      const { name, result } = e.detail || {};
      fireToast(`GQ ran ${name} · ${String(result || '').slice(0, 60)}`);
    };
    window.addEventListener('gq:tool-executed', handler);
    return () => window.removeEventListener('gq:tool-executed', handler);
  }, [fireToast]);

  // Pages can request the Ask GQ panel via this event (e.g. vault "Generate a strategy")
  React.useEffect(() => {
    const handler = () => setChatOpen(true);
    window.addEventListener('gq:open-chat', handler);
    return () => window.removeEventListener('gq:open-chat', handler);
  }, []);

  const navigate = (id, params = {}) => {
    if (window.navigateTo) window.navigateTo(id, params);
    else if (window.GQ?.state?.setHash) window.GQ.state.setHash(id, params);
    else window.location.hash = id;
    setRoute(id);
    setDrawer(false);
  };

  const TITLES = {
    vault:"My strategy vault", tearsheet:"Performance tearsheet",
    marketplace:"Marketplace", designer:"Strategy lab",
    optimize:"GQ™ Optimize", stress:"Stress test",
    settings:"Settings", exchanges:"Exchanges", admin:"Admin dashboard",
    "strategy-detail":"Strategy detail", mint:"Mint a strategy",
    backtest:"Backtest", compare:"Compare strategies",
  };

  const handleSignOut = async () => {
    await window.gqAuth.signOut();
    window.location.href = "auth.html";
  };

  if (!authChecked) return (
    <div style={{ height:"100vh", display:"flex", alignItems:"center", justifyContent:"center", gap:12 }}>
      <div style={{ width:20, height:20, borderRadius:"50%", border:"2px solid var(--primary)", borderTopColor:"transparent", animation:"spin 0.8s linear infinite" }}/>
      <span style={{ fontFamily:"var(--font-ui)", color:"var(--muted)", fontSize:13 }}>Checking access…</span>
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const handleNav = (r) => {
    if (r === "__help") { setShortcutsOpen(true); return; }
    navigate(r);
  };

  return (
    <>
      <DevModeBanner/>
      <MobileBanner/>
      <CookieBanner/>
      {tourOpen && <WelcomeTour onClose={() => setTourOpen(false)}/>}
      <CommandPalette open={paletteOpen} onClose={()=>setPaletteOpen(false)} onNavigate={handleNav} onAskGQ={()=>setChatOpen(true)}/>
      <ShortcutsModal open={shortcutsOpen} onClose={()=>setShortcutsOpen(false)}/>
    <div className="shell">
      <AppTopBar
        title={TITLES[route] || "GQ Strategy Builder™"}
        onMenu={() => setDrawer(true)}
        onSignOut={handleSignOut}
        isAdmin={role === "admin"}
        onAdmin={() => navigate("admin")}
        onAskGQ={() => setChatOpen(true)}
      />
      <AppSidebar
        open={drawer}
        active={route}
        onNavigate={navigate}
        onClose={() => setDrawer(false)}
        isAdmin={role === "admin"}
      />
      <main className="main">
        <div className="page-enter" key={route}>
          {route === "vault"            && <VaultPage           onToast={fireToast} onNavigate={navigate}/>}
          {route === "tearsheet"        && <TearsheetPage       onToast={fireToast} onNavigate={navigate}/>}
          {route === "designer"         && <DesignerPage        onToast={fireToast}/>}
          {route === "optimize"         && <OptimizePage        onToast={fireToast}/>}
          {route === "stress"           && <StressPage          onToast={fireToast}/>}
          {route === "marketplace"      && <MarketplacePage     onToast={fireToast}/>}
          {route === "settings"         && <SettingsPage        onToast={fireToast}/>}
          {route === "exchanges"        && <SettingsPage        onToast={fireToast} initialTab="exchanges"/>}
          {route === "strategy-detail"  && <StrategyDetailPage  onToast={fireToast} onNavigate={navigate}/>}
          {route === "mint"             && <MintStrategyPage    onToast={fireToast} onNavigate={navigate}/>}
          {route === "backtest"         && <BacktestPage        onToast={fireToast} onNavigate={navigate}/>}
          {route === "compare"          && <ComparePage         onToast={fireToast}/>}
          {route === "admin" && role === "admin" && <AdminPage  onToast={fireToast}/>}
        </div>
      </main>
      <AskGQChat open={chatOpen} onClose={()=>setChatOpen(false)} page={TITLES[route]||"GQ"} route={route}/>
      {!chatOpen && (
        <button onClick={()=>setTourOpen(true)} aria-label="Open welcome tour" title="Welcome tour" style={{
          position:"fixed", bottom:78, right:24, zIndex:4998,
          width:32, height:32,
          borderRadius:9999, border:"1px solid rgba(255,255,255,0.10)", cursor:"pointer",
          background:"rgba(15,15,15,0.92)", backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
          display:"flex", alignItems:"center", justifyContent:"center",
          color:"rgba(245,245,247,0.6)",
          transition:"all .18s ease",
        }}
        onMouseEnter={e=>{ e.currentTarget.style.color="var(--ink)"; e.currentTarget.style.borderColor="rgba(255,255,255,0.20)"; }}
        onMouseLeave={e=>{ e.currentTarget.style.color="rgba(245,245,247,0.6)"; e.currentTarget.style.borderColor="rgba(255,255,255,0.10)"; }}
        >
          <HelpCircle size={14}/>
        </button>
      )}
      {!chatOpen && (
        <button onClick={()=>setChatOpen(true)} aria-label="Ask GQ AI" style={{
          position:"fixed", bottom:24, right:24, zIndex:4999,
          height:44, padding:"0 16px 0 14px",
          borderRadius:9999, border:"1px solid rgba(217,70,239,0.30)", cursor:"pointer",
          background:"rgba(15,15,15,0.92)", backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
          display:"flex", alignItems:"center", gap:9,
          transition:"all .18s ease",
          boxShadow:"0 12px 36px rgba(0,0,0,0.4), 0 0 0 1px rgba(217,70,239,0.08), inset 0 0 0 1px rgba(255,255,255,0.02)",
        }}
        onMouseEnter={e=>{ e.currentTarget.style.borderColor="rgba(217,70,239,0.55)"; e.currentTarget.style.transform="translateY(-1px)"; }}
        onMouseLeave={e=>{ e.currentTarget.style.borderColor="rgba(217,70,239,0.30)"; e.currentTarget.style.transform="translateY(0)"; }}
        >
          <span style={{
            width:24, height:24, borderRadius:"50%",
            background:"linear-gradient(135deg, #c4b5fd, #D946EF)",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontFamily:"'Inter Tight', 'Inter', sans-serif", fontWeight:800,
            fontSize:11, color:"#0a0a0a", letterSpacing:"-0.02em",
            boxShadow:"0 0 0 1px rgba(255,255,255,0.08) inset",
          }}>GQ</span>
          <span style={{
            fontFamily:"var(--font-ui)", fontSize:13, fontWeight:500,
            color:"var(--ink)", letterSpacing:"-0.005em",
          }}>Ask</span>
          <span style={{
            fontFamily:"var(--font-mono)", fontSize:10,
            color:"var(--muted)", padding:"2px 6px",
            background:"rgba(255,255,255,0.04)", border:"1px solid var(--border)",
            borderRadius:4, marginLeft:2,
          }}>A</span>
        </button>
      )}
      <footer style={{
        padding: "12px 24px",
        borderTop: "1px solid var(--border)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
      }}>
        <span style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)" }}>
          © 2025 Genetic Quant AI · Private alpha · Paper trading only
        </span>
        <div style={{ display:"flex", gap:16, alignItems:"center" }}>
          {[
            { label:"Terms", href:"../terms.html" },
            { label:"Privacy", href:"../privacy.html" },
            { label:"Disclaimer", href:"../app/legal/disclaimer.html" },
          ].map(l => (
            <a key={l.label} href={l.href}
              style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)",
                textDecoration:"none", transition:"color .15s" }}
              onMouseEnter={e=>e.currentTarget.style.color="var(--ink)"}
              onMouseLeave={e=>e.currentTarget.style.color="var(--muted)"}
            >{l.label}</a>
          ))}
          <button key="Feedback" onClick={()=>setFeedbackOpen(true)} style={{
            background:"none", border:"none", cursor:"pointer",
            fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)",
            transition:"color .15s", padding:0,
          }} onMouseEnter={e=>e.currentTarget.style.color="var(--ink)"}
             onMouseLeave={e=>e.currentTarget.style.color="var(--muted)"}>Feedback</button>
        </div>
      </footer>
      {feedbackOpen && (
        <div style={{
          position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", backdropFilter:"blur(8px)",
          zIndex:8000, display:"flex", alignItems:"center", justifyContent:"center", padding:24,
        }} onClick={e=>{ if(e.target===e.currentTarget) setFeedbackOpen(false); }}>
          <div style={{
            maxWidth:400, width:"100%", background:"var(--bg-card)",
            border:"1px solid var(--border-strong)", borderRadius:"var(--r-lg)", padding:28,
          }}>
            <h3 style={{fontFamily:"'Inter Tight','Inter',sans-serif", fontSize:18, fontWeight:700,
              color:"var(--ink)", marginBottom:6, letterSpacing:"-0.02em"}}>Send feedback</h3>
            <p style={{fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)", marginBottom:16}}>
              Private alpha · your feedback shapes the roadmap
            </p>
            <FeedbackForm onClose={()=>setFeedbackOpen(false)} onToast={fireToast}/>
          </div>
        </div>
      )}
      {ToastEl}
    </div>
    </>
  );
}

window.App = App;
